import { FC, Fragment, memo, MouseEvent, useEffect, useMemo, useState } from 'react';
import { Dialog, Divider } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useTranslation } from 'react-i18next';
import { useReleaseNotesRecentQuery } from './useReleaseNotesQuery';
import { ReleaseNotesEntry } from './Entry';
import { withErrorBoundary } from '~/hocs/withErrorBoundary';
import { useSelector } from '~/hooks';

const storageKey = 'RELEASE_NOTES_LAST_OPENED';

const Content = styled.div`
  ul {
    padding-inline-start: 16px;
  }
  li {
    margin-block-start: 0.6em;
    margin-block-end: 0.6em;
  }
  li p {
    margin: 0;
  }
  p,
  li {
    line-height: ${({ theme }) => theme.lineHeights.$24};
    font-size: ${({ theme }) => theme.fontSizes.$14};
    margin-block-start: 1em;
    margin-block-end: 1em;
  }
  p a {
    color: ${({ theme }) => theme.colors.content.accent.color};
    &:hover {
      color: ${({ theme }) => theme.colors.content.accent.hovered.color};
    }
  }

  img {
    max-width: 100% !important;
    height: auto !important;
  }
`;

const ReleaseNotesBase: FC = () => {
  const { t } = useTranslation();
  const userCreateTs = useSelector(({ userResources }) => userResources.users.current.create_ts);

  const [open, setOpen] = useState(false);
  const { data, refetch } = useReleaseNotesRecentQuery({ enabled: false });

  /**
   * Fetch release info with delay so other app parts are loaded first
   */
  useEffect(() => {
    if (import.meta.env.VITE_DISABLE_THIRD_PARTY_SCRIPTS === 'true') return undefined;
    const timeout = setTimeout(refetch, 3000);
    return () => clearTimeout(timeout);
  }, [refetch]);

  const lastOpened = useMemo(() => {
    const releaseNotesLastOpened = window.localStorage.getItem(storageKey);
    return releaseNotesLastOpened
      ? Number(releaseNotesLastOpened)
      : new Date(userCreateTs).getTime() / 1000;
  }, [userCreateTs]);

  const newestEntry = data?.releases?.[0]?.released_at;
  const hasNewRelease = !!(newestEntry && newestEntry > lastOpened);

  useEffect(() => {
    if (hasNewRelease) {
      setOpen(true);
      window.localStorage.setItem(storageKey, String(newestEntry));
    }
  }, [hasNewRelease, newestEntry]);

  const releases = (data?.releases ?? [])
    .filter(({ private: privateRelease }) => !privateRelease)
    .slice(0, 3);

  /**
   * Capture all link clicks events and force-open them in new tab
   */
  const onContentClick = (event: MouseEvent<HTMLElement>) => {
    const closestLink = (event.target as HTMLElement).closest('a');
    if (closestLink && closestLink.href) {
      event.preventDefault();
      window.open(closestLink.href, '_blank');
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} width={780} initialFocus={-1}>
      <Dialog.Header>
        <Dialog.Header.Title title={t('releaseNotesTitle')} />
      </Dialog.Header>
      <Dialog.Content>
        <Content onClick={onContentClick}>
          {releases.map((release, index) => (
            <Fragment key={release.id}>
              <ReleaseNotesEntry release={release} />
              {index !== releases.length - 1 && <Divider />}
            </Fragment>
          ))}
        </Content>
      </Dialog.Content>
    </Dialog>
  );
};

export const ReleaseNotes = memo(withErrorBoundary(ReleaseNotesBase));
